import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { onAuthStateChanged, FirebaseUserType, limit } from '../services/firebaseImports';
import { getFailoverAwareDatabase, getFailoverAwareAuth } from '../config/firebaseWithFailover';
import Logger from '../services/logger';
import { LOAD_STATUS, PO_STATUS, SHIPPING_SCHEDULE_STATUS } from '../constants/statusValues';
import {
  resolveShipperScheduleCarrierLabel,
  resolveShipperScheduleTypeLabel,
} from '../utils/carrierPartnerRequestPoGate';
import {
  getShipperOwnedPurchaseOrderDocs,
  getShipperParentMultiLoadPurchaseOrder,
  subscribeToShipperOwnedPurchaseOrders,
} from '../services/purchaseOrderFirestoreQueries';

export interface ScheduledShipment {
  id: string;
  date: string;
  time: string;
  destination: string;
  pickup?: string;
  carrier: string;
  status: 'Open' | 'Carrier Pending' | 'Active' | 'Completed';
  type: string;
  shipTo?: string;
  cost: number;
  poNumber: string;
  pickupLocation?: any;
  deliveryLocation?: any;
  // Fields for Carrier Review button logic
  carrierOption?: 'carrier' | 'marketplace';
  isMarketplace?: boolean;
  pendingCarrier?: { id: string; [key: string]: any };
  approvedCarrier?: { id: string; [key: string]: any };
  // Phase 6 / 8F: Rate confirmation audit + PDF status (from PO)
  rateConfirmationAcceptedAt?: string;
  rateConfirmationAcceptedBy?: string;
  rateConfirmationSignatureName?: string;
  rateConfirmationPdfStatus?: string;
  rateConfirmationPdfStoragePath?: string;
  rateConfirmationPdfError?: string;
}

interface ShipmentsContextType {
  shipments: ScheduledShipment[];
  loading: boolean;
  error: string | null;
  refreshShipments: () => Promise<void>;
}

const ShipmentsContext = createContext<ShipmentsContextType | undefined>(undefined);

export const ShipmentsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [shipments, setShipments] = useState<ScheduledShipment[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  
  const parentStatusCache = React.useRef<Map<string, { status: string; shippingScheduleStatus: string; timestamp: number }>>(new Map());
  const shipmentsListenerRef = React.useRef<(() => void) | null>(null);
  const CACHE_TTL = 60000; // 60 seconds cache TTL
  
  // FIXED: Cleanup expired cache entries periodically (Phase 4)
  React.useEffect(() => {
    const cleanupInterval = setInterval(() => {
      const now = Date.now();
      const cache = parentStatusCache.current;
      const expiredKeys: string[] = [];
      
      cache.forEach((value, key) => {
        if (now - value.timestamp > CACHE_TTL) {
          expiredKeys.push(key);
        }
      });
      
      expiredKeys.forEach(key => cache.delete(key));
      
      if (expiredKeys.length > 0) {
        Logger.debug(`[ShipmentsContext] Cleaned up ${expiredKeys.length} expired cache entries`);
      }
    }, 30000); // Clean up every 30 seconds
    
    return () => clearInterval(cleanupInterval);
  }, []);
  
  // FIXED: Debounce function to prevent rapid updates
  const debounceRef = React.useRef<NodeJS.Timeout | null>(null);
  const debounceUpdate = React.useCallback((updateFn: () => void, delay: number = 300) => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(updateFn, delay);
  }, []);

  // FIXED: Memoize refreshShipments to prevent unnecessary re-renders (Phase 4)
  const refreshShipments = useCallback(async () => {
    const db = getFailoverAwareDatabase();
    const auth = getFailoverAwareAuth();
    if (!db || !auth) {
      setError('Database or auth not available');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // Get current user - refreshShipments needs user context for filtering
      const currentUser = auth.currentUser;
      if (!currentUser) {
        setShipments([]);
        setLoading(false);
        return;
      }
    
        const statusMap: { [key: string]: ScheduledShipment['status'] } = {
          'open': PO_STATUS.OPEN,
          'carrier pending': SHIPPING_SCHEDULE_STATUS.CARRIER_PENDING,
          'carrier pending/approval': SHIPPING_SCHEDULE_STATUS.CARRIER_PENDING,
          'active': LOAD_STATUS.ACTIVE,
          'completed': LOAD_STATUS.COMPLETED,
        };
    
    // FIXED: Add user filtering and limit to prevent unbounded query
    // Bypass date requirement - query without orderBy to allow POs without dates
    const purchaseOrderDocs = await getShipperOwnedPurchaseOrderDocs(db, currentUser.uid, [
      limit(100),
    ]);
    const allPOs = purchaseOrderDocs.map((doc) => ({
      id: doc.id,
      data: doc.data() as any,
    }));
    
      // Build parent PO status cache for efficient lookup
      const currentParentStatusCache = new Map<string, { status: string; shippingScheduleStatus: string }>();
      const parentPONumbers = new Set<string>();
      
      allPOs.forEach(({ data }: { id: string; data: any }) => {
        if (data.isMultiLoad && data.parentPONumber) {
          parentPONumbers.add(data.parentPONumber);
        }
      });
      
      // Fetch parent PO statuses (with cache check)
      if (parentPONumbers.size > 0) {
        const now = Date.now();
        const parentPONumbersToFetch = Array.from(parentPONumbers).filter(poNumber => {
          const cached = parentStatusCache.current.get(poNumber);
          if (cached && (now - cached.timestamp) < CACHE_TTL) {
            // Use cached value
            currentParentStatusCache.set(poNumber, {
              status: cached.status,
              shippingScheduleStatus: cached.shippingScheduleStatus
            });
            return false; // Don't fetch
          }
          return true; // Need to fetch
        });
        
        if (parentPONumbersToFetch.length > 0) {
          const parentSnapshots = await Promise.all(
            parentPONumbersToFetch.map((parentPONumber) =>
              getShipperParentMultiLoadPurchaseOrder(db, parentPONumber, currentUser.uid)
            )
          );
          parentSnapshots.forEach((parentDoc) => {
            if (!parentDoc) return;
            const data = parentDoc.data();
            const statusData = {
              status: data.status || '',
              shippingScheduleStatus: data.shippingScheduleStatus || data.status || '',
            };
            currentParentStatusCache.set(data.poNumber, statusData);
            parentStatusCache.current.set(data.poNumber, {
              ...statusData,
              timestamp: now,
            });
          });
        }
      }
    
      const shipmentsData = allPOs
        .filter(({ data }: { id: string; data: any }) => {
          // Filter out parent POs - only show child POs and single-load POs
          // Parent POs are organizational only and shouldn't appear on Shipping Schedule
          if (data.isMultiLoad && !data.parentPONumber) {
            return false;
          }
          
          // Check both status fields
          const mainStatus = (data.status || '').toLowerCase();
          const scheduleStatus = (data.shippingScheduleStatus || data.status || '').toLowerCase();
          
          // CRITICAL FIX: Filter out archived POs (matches Purchase Orders page)
          // Archived POs are completed and archived by shipper clicking "Complete PO"
          if (data.archivedAt != null) {
            return false;
          }
          
          // FIXED: Filter out deleted, cancelled, and completed to match PurchaseOrders page filtering
          if (mainStatus === 'deleted' || mainStatus === PO_STATUS.CANCELLED.toLowerCase() || mainStatus === PO_STATUS.COMPLETED.toLowerCase() || 
              scheduleStatus === 'deleted' || scheduleStatus === SHIPPING_SCHEDULE_STATUS.CANCELLED.toLowerCase() || scheduleStatus === SHIPPING_SCHEDULE_STATUS.COMPLETED.toLowerCase()) {
            return false;
          }
          
          // For child POs, check parent status
          if (data.isMultiLoad && data.parentPONumber) {
            const parentStatus = currentParentStatusCache.get(data.parentPONumber);
          if (parentStatus) {
            const parentMainStatus = (parentStatus.status || '').toLowerCase();
            const parentScheduleStatus = (parentStatus.shippingScheduleStatus || '').toLowerCase();
            // Hide child if parent is cancelled or completed
            if (parentMainStatus === PO_STATUS.CANCELLED.toLowerCase() || parentMainStatus === PO_STATUS.COMPLETED.toLowerCase() ||
                parentScheduleStatus === SHIPPING_SCHEDULE_STATUS.CANCELLED.toLowerCase() || parentScheduleStatus === SHIPPING_SCHEDULE_STATUS.COMPLETED.toLowerCase()) {
              return false;
            }
          } else {
            // Parent not found in cache - this means parent was deleted/completed or doesn't exist
            // RULE: No parent PO = No child PO should show
            Logger.warn(`Parent PO ${data.parentPONumber} not found for child PO ${data.poNumber}, hiding child`, { parentPONumber: data.parentPONumber, poNumber: data.poNumber });
            return false; // Hide child if parent doesn't exist
          }
        }
        
        return true;
      })
      .map(({ id, data }: { id: string; data: any }) => {
        const normalizedStatus = (data.shippingScheduleStatus || data.status || '').toLowerCase();
        const status: ScheduledShipment['status'] = statusMap[normalizedStatus] || PO_STATUS.OPEN;
        return {
          id,
          date: data.date || '',
          time: data.scheduledTime || '',
          destination: data.shipTo?.cityStateZip || '',
          pickup: data.vendorInfo?.cityStateZip || data.pickupLocation?.address || '',
          carrier: resolveShipperScheduleCarrierLabel(data as Record<string, unknown>),
          status,
          type: resolveShipperScheduleTypeLabel(data as Record<string, unknown>),
          shipTo: data.shipTo?.name || '',
          cost: typeof data.rate === 'number' ? data.rate : (typeof data.total === 'number' ? data.total : 0),
          poNumber: data.poNumber || '',
          // CRITICAL FIX: Pass through fields needed for Carrier Review button logic
          carrierOption: data.carrierOption,
          isMarketplace: data.isMarketplace === true || data.carrierOption === 'marketplace',
          pendingCarrier: data.pendingCarrier,
          approvedCarrier: data.approvedCarrier,
          rateConfirmationAcceptedAt: data.rateConfirmationAcceptedAt,
          rateConfirmationAcceptedBy: data.rateConfirmationAcceptedBy,
          rateConfirmationSignatureName: data.rateConfirmationSignatureName,
          rateConfirmationPdfStatus: data.rateConfirmationPdfStatus,
          rateConfirmationPdfStoragePath: data.rateConfirmationPdfStoragePath,
          rateConfirmationPdfError: data.rateConfirmationPdfError,
          } as ScheduledShipment;
        })
        .sort((a: ScheduledShipment, b: ScheduledShipment) => {
          // Client-side sorting by date (descending) - handle missing dates
          const dateA = a.date || '';
          const dateB = b.date || '';
          if (!dateA && !dateB) return 0;
          if (!dateA) return 1; // POs without dates go to end
          if (!dateB) return -1;
          return dateB.localeCompare(dateA); // Descending order
        });
      setShipments(shipmentsData);
      setLoading(false);
    } catch (err: any) {
      Logger.error('Error refreshing shipments:', err instanceof Error ? err : new Error(String(err)), { err });
      setError(err.message || 'Failed to refresh shipments');
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const auth = getFailoverAwareAuth();
    const unsubscribeAuth = onAuthStateChanged(auth, (user: FirebaseUserType | null) => {
      if (shipmentsListenerRef.current) {
        shipmentsListenerRef.current();
        shipmentsListenerRef.current = null;
      }
      if (user) {
        const db = getFailoverAwareDatabase();
        const unsub = subscribeToShipperOwnedPurchaseOrders(
          db,
          user.uid,
          [limit(100)],
          (purchaseOrderDocs) => {
          debounceUpdate(() => {
            (async () => {
              try {
                setLoading(true);
        const statusMap: { [key: string]: ScheduledShipment['status'] } = {
          'open': PO_STATUS.OPEN,
          'carrier pending': SHIPPING_SCHEDULE_STATUS.CARRIER_PENDING,
          'carrier pending/approval': SHIPPING_SCHEDULE_STATUS.CARRIER_PENDING,
          'active': LOAD_STATUS.ACTIVE,
          'completed': LOAD_STATUS.COMPLETED,
        };
                
                const allPOs = purchaseOrderDocs.map((doc) => ({
                  id: doc.id,
                  data: doc.data() as any,
                }));
                
                // Build parent PO status cache for efficient lookup (with cache check)
                const currentParentStatusCache = new Map<string, { status: string; shippingScheduleStatus: string }>();
                const parentPONumbers = new Set<string>();
                
                allPOs.forEach(({ data }: { id: string; data: any }) => {
                  if (data.isMultiLoad && data.parentPONumber) {
                    parentPONumbers.add(data.parentPONumber);
                  }
                });
                
                // Fetch parent PO statuses if needed (with cache)
                if (parentPONumbers.size > 0) {
                  try {
                    const now = Date.now();
                    const parentPONumbersToFetch = Array.from(parentPONumbers).filter(poNumber => {
                      const cached = parentStatusCache.current.get(poNumber);
                      if (cached && (now - cached.timestamp) < CACHE_TTL) {
                        // Use cached value
                        currentParentStatusCache.set(poNumber, {
                          status: cached.status,
                          shippingScheduleStatus: cached.shippingScheduleStatus
                        });
                        return false; // Don't fetch
                      }
                      return true; // Need to fetch
                    });
                    
                    if (parentPONumbersToFetch.length > 0) {
                      const parentSnapshots = await Promise.all(
                        parentPONumbersToFetch.map((parentPONumber) =>
                          getShipperParentMultiLoadPurchaseOrder(db, parentPONumber, user.uid)
                        )
                      );
                      parentSnapshots.forEach((parentDoc) => {
                        if (!parentDoc) return;
                        const data = parentDoc.data();
                        const statusData = {
                          status: data.status || '',
                          shippingScheduleStatus: data.shippingScheduleStatus || data.status || '',
                        };
                        currentParentStatusCache.set(data.poNumber, statusData);
                        parentStatusCache.current.set(data.poNumber, {
                          ...statusData,
                          timestamp: now,
                        });
                      });
                    }
                  } catch (error) {
                    Logger.warn('Error fetching parent PO statuses:', { error });
                    // Continue without parent status check if it fails
                  }
                }
                
                const shipmentsData = allPOs
                  .filter(({ data }: { id: string; data: any }) => {
                    // Filter out parent POs - only show child POs and single-load POs
                    // Parent POs are organizational only and shouldn't appear on Shipping Schedule
                    if (data.isMultiLoad && !data.parentPONumber) {
                      return false;
                    }
                    
                    // Check both status fields
                    const mainStatus = (data.status || '').toLowerCase();
                    const scheduleStatus = (data.shippingScheduleStatus || data.status || '').toLowerCase();
                    
                    // CRITICAL FIX: Filter out archived POs (matches Purchase Orders page)
                    // Archived POs are completed and archived by shipper clicking "Complete PO"
                    if (data.archivedAt != null) {
                      return false;
                    }
                    
                    // FIXED: Filter out deleted, cancelled, and completed to match PurchaseOrders page filtering
                    if (mainStatus === 'deleted' || mainStatus === PO_STATUS.CANCELLED.toLowerCase() || mainStatus === PO_STATUS.COMPLETED.toLowerCase() || 
                        scheduleStatus === 'deleted' || scheduleStatus === SHIPPING_SCHEDULE_STATUS.CANCELLED.toLowerCase() || scheduleStatus === SHIPPING_SCHEDULE_STATUS.COMPLETED.toLowerCase()) {
                      return false;
                    }
                    
                    // For child POs, check parent status
                    if (data.isMultiLoad && data.parentPONumber) {
                      const parentStatus = currentParentStatusCache.get(data.parentPONumber);
                      if (parentStatus) {
                        const parentMainStatus = (parentStatus.status || '').toLowerCase();
                        const parentScheduleStatus = (parentStatus.shippingScheduleStatus || '').toLowerCase();
                        // Hide child if parent is cancelled or completed
                        if (parentMainStatus === PO_STATUS.CANCELLED.toLowerCase() || parentMainStatus === PO_STATUS.COMPLETED.toLowerCase() ||
                            parentScheduleStatus === SHIPPING_SCHEDULE_STATUS.CANCELLED.toLowerCase() || parentScheduleStatus === SHIPPING_SCHEDULE_STATUS.COMPLETED.toLowerCase()) {
                          return false;
                        }
                      } else {
                        // Parent not found in cache - this means parent was deleted/completed or doesn't exist
                        // RULE: No parent PO = No child PO should show
                        Logger.warn(`Parent PO ${data.parentPONumber} not found for child PO ${data.poNumber}, hiding child`, { parentPONumber: data.parentPONumber, poNumber: data.poNumber });
                        return false; // Hide child if parent doesn't exist
                      }
                    }
                    
                    return true;
                  })
                  .map(({ id, data }: { id: string; data: any }) => {
                    const normalizedStatus = (data.shippingScheduleStatus || data.status || '').toLowerCase();
                    const status: ScheduledShipment['status'] = statusMap[normalizedStatus] || PO_STATUS.OPEN;
                    return {
                      id,
                      date: data.date || '',
                      time: data.scheduledTime || '',
                      destination: data.shipTo?.cityStateZip || '',
                      pickup: data.vendorInfo?.cityStateZip || data.pickupLocation?.address || '',
                      carrier: resolveShipperScheduleCarrierLabel(data as Record<string, unknown>),
                      status,
                      type: resolveShipperScheduleTypeLabel(data as Record<string, unknown>),
                      shipTo: data.shipTo?.name || '',
                      cost: typeof data.rate === 'number' ? data.rate : (typeof data.total === 'number' ? data.total : 0),
                      poNumber: data.poNumber || '',
                      // CRITICAL FIX: Pass through fields needed for Carrier Review button logic
                      carrierOption: data.carrierOption,
                      isMarketplace: data.isMarketplace === true || data.carrierOption === 'marketplace',
                      pendingCarrier: data.pendingCarrier,
                      approvedCarrier: data.approvedCarrier,
                      rateConfirmationAcceptedAt: data.rateConfirmationAcceptedAt,
                      rateConfirmationAcceptedBy: data.rateConfirmationAcceptedBy,
                      rateConfirmationSignatureName: data.rateConfirmationSignatureName,
                      rateConfirmationPdfStatus: data.rateConfirmationPdfStatus,
                      rateConfirmationPdfStoragePath: data.rateConfirmationPdfStoragePath,
                      rateConfirmationPdfError: data.rateConfirmationPdfError,
                    } as ScheduledShipment;
                  })
                  .sort((a: ScheduledShipment, b: ScheduledShipment) => {
                    // Client-side sorting by date (descending) - handle missing dates
                    const dateA = a.date || '';
                    const dateB = b.date || '';
                    if (!dateA && !dateB) return 0;
                    if (!dateA) return 1; // POs without dates go to end
                    if (!dateB) return -1;
                    return dateB.localeCompare(dateA); // Descending order
                  });
                setShipments(shipmentsData);
                setLoading(false);
                setError(null);
              } catch (err: any) {
                Logger.error('Error processing shipment updates:', err instanceof Error ? err : new Error(String(err)), { err });
                setError(err.message || 'Failed to process shipment updates');
                setLoading(false);
              }
            })();
          }, 300); // 300ms debounce
          },
          (err) => {
            Logger.error('[ShipmentsContext] Shipper PO subscription error:', err);
            setError(err.message || 'Failed to subscribe to shipments');
            setShipments([]);
            setLoading(false);
          }
        );
        shipmentsListenerRef.current = unsub;
      } else {
        setShipments([]);
      }
    });
    return () => {
      if (shipmentsListenerRef.current) {
        shipmentsListenerRef.current();
        shipmentsListenerRef.current = null;
      }
      unsubscribeAuth();
    };
  }, []);

  // Cleanup debounce on unmount
  React.useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  // FIXED: Memoize context value to prevent unnecessary re-renders (Phase 4)
  const contextValue = useMemo(() => ({
    shipments,
    loading,
    error,
    refreshShipments,
  }), [shipments, loading, error, refreshShipments]);

  return (
    <ShipmentsContext.Provider value={contextValue}>
      {children}
    </ShipmentsContext.Provider>
  );
};

export const useShipments = () => {
  const context = useContext(ShipmentsContext);
  if (!context) {
    throw new Error('useShipments must be used within a ShipmentsProvider');
  }
  return context;
}; 